"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _classnames = _interopRequireDefault(require("classnames"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _clipboardMin = _interopRequireDefault(require("clipboard/dist/clipboard.min.js"));

var _icon = _interopRequireDefault(require("../icon"));

var _popover = _interopRequireDefault(require("../popover"));

var _tooltip = _interopRequireDefault(require("../tooltip"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const FEEDBACK_TIME = 800;
/**
 * A button that, when clicked, copies the designated text to the clipboard.
 *
 * The static function `CopyButton.isCopySupported` returns a boolean
 * indicating whether the current browser will support automatic copying.
 * If it does not, you might want to hide your copy button and make sure
 * the user is able to manually select and copy the text.
 */

class CopyButton extends _react.default.PureComponent {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      showingFeedback: false
    });

    _defineProperty(this, "handleCopyButtonClick", event => {
      // Clipboard.js attaches its own click handlers for copying
      const {
        onCopy,
        text
      } = this.props;

      if (onCopy) {
        onCopy(text);
      }

      this.showFeedback();
      this.destroyClipboard(); // destroy clipboard

      if (event) this.setClipboard(event.target); // reinitialize clipboard
    });

    _defineProperty(this, "showFeedback", () => {
      this.setState({
        showingFeedback: true
      });
      this.revertTimer = setTimeout(() => {
        this.setState({
          showingFeedback: false
        });
      }, FEEDBACK_TIME);
    });

    _defineProperty(this, "setContainer", element => {
      if (!element) return;
      const {
        textEl
      } = this.props;
      this.container = element;

      if (!textEl && _clipboardMin.default.isSupported()) {
        this.setClipboard(element);
      }
    });

    _defineProperty(this, "getContainer", () => {
      return this.container;
    });

    _defineProperty(this, "destroyClipboard", () => {
      if (this.clipboard) {
        this.clipboard.destroy();
      }
    });
  }

  componentDidMount() {
    if (this.props.textEl && _clipboardMin.default.isSupported()) {
      this.setClipboard(this.props.textEl);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.revertTimer);
    this.destroyClipboard();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const copyAvailable = _clipboardMin.default.isSupported();

    if (nextProps.textEl !== this.props.textEl && copyAvailable) {
      this.setClipboard(nextProps.textEl);
    }

    if (!copyAvailable) {
      this.destroyClipboard();
    }
  }

  setClipboard(element) {
    const {
      focusTrapPaused
    } = this.props;
    this.clipboard = new _clipboardMin.default(element, { // Setting the container is necessary for Clipboard to function within
      // focus traps, like in a Modal.
      ...(!focusTrapPaused && {
        container: element
      })
    });
  }

  renderFeedbackPopover() {
    const {
      state
    } = this;

    if (!state.showingFeedback) {
      return null;
    }

    return /*#__PURE__*/_react.default.createElement(_popover.default // We don't need this one to be as accessible as a regular tooltip
    , {
      getAnchorElement: this.getContainer,
      placement: "top",
      alignment: "center",
      hideWhenAnchorIsOffscreen: true,
      accessibleTitle: null,
      padding: "small"
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "txt-s"
    }, "Copied!"));
  }

  render() {
    const {
      props,
      state
    } = this; // don't render the button if you can't use it to copy,
    // or are in Node during a static build

    if (typeof document !== 'undefined' && !_clipboardMin.default.isSupported()) {
      return null;
    }

    const iconName = state.showingFeedback ? 'check' : 'clipboard';
    const buttonClasses = (0, _classnames.default)(props.className, {
      block: props.block
    }); // data-clipboard-text and the container ref are used by clipboard.js
    // to copy text if you do not pass in a textEl as a prop.
    // Note that this wont have as nice a failure mode.

    return /*#__PURE__*/_react.default.createElement(_tooltip.default, {
      disabled: state.showingFeedback,
      content: "Copy",
      block: props.block
    }, /*#__PURE__*/_react.default.createElement("button", _extends({
      "aria-label": "Copy",
      type: "button",
      className: buttonClasses
    }, props.passthroughProps, {
      ref: this.setContainer,
      "data-clipboard-text": props.text,
      onClick: this.handleCopyButtonClick
    }), /*#__PURE__*/_react.default.createElement(_icon.default, {
      name: iconName
    }), this.renderFeedbackPopover()));
  }

}

exports.default = CopyButton;
CopyButton.propTypes = {
  /**
   * The text that will be copied when the button is clicked.
   */
  text: _propTypes.default.string.isRequired,

  /**
   * Pass in an element containing text to copy instead of the raw text
   * to provide a better fallback, where the text is selected for you if
   * `execcopy` is not supported.
   */
  textEl: _propTypes.default.object,

  /**
   * Invoked when the button is clicked.
   * Passed one argument: the `text` prop.
   */
  onCopy: _propTypes.default.func,

  /**
   * If `true`, the element will be `block` displayed instead of `inline-block`.
   *
   * This is sometimes necessary to get your pixel-perfect layout, if you don't
   * want the extra line-height that wraps inline elements. Typically, you
   * should only set `block` to `true` if the parent element is controlling
   * width (in a layout that uses flexbox, absolute positioning, or floats).
   */
  block: _propTypes.default.bool,

  /**
   * If `true`, this will allow interaction with elements outside of the
   * modal container. You normally don't want to set this, but it can be
   * useful for nesting different components that are displaced to other
   * parts of the DOM.
   */
  focusTrapPaused: _propTypes.default.bool,

  /**
   * The `className` prop of the `<button>`.
   */
  className: _propTypes.default.string,

  /**
   * An object of props that you want to pass through to the `<button>`.
   */
  passthroughProps: _propTypes.default.object
};
CopyButton.defaultProps = {
  className: 'btn btn--xs py3 px3 round',
  block: false
};

CopyButton.isCopySupported = () => {
  return _clipboardMin.default.isSupported();
};